import React, { Suspense } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import FallbackSpinner from "./@core/components/spinner";

import AuthGuard from "src/@core/components/auth/AuthGuard";
import UserLayout from "./layouts/UserLayout";
import BlankLayout from "./@core/layouts/BlankLayout";
// import BlankLayoutWithAppBar from './@core/layouts/BlankLayoutWithAppBar'
import AclGuard from "./@core/components/auth/AclGuard";
import GuestGuard from "./@core/components/auth/GuestGuard";
import { defaultACLObj } from "./configs/acl";
import AuthLayout from "./layouts/AuthLayout";

const HomePage = React.lazy(() => import("./pages/home"));
const DashBoardPage = React.lazy(() => import("./pages/dashboard"));
const HomeDetailsPage = React.lazy(() => import("./pages/home-details"));
const BannerImagesPage = React.lazy(() => import("./pages/banner"));
const PerformaxPage = React.lazy(() => import("./pages/performax"));
const GalleryPage = React.lazy(() => import("./pages/gallery"));
const WorkshopPage = React.lazy(() => import("./pages/workshop"));
const ServicePage = React.lazy(() => import("./pages/service"));
const InquiryPage = React.lazy(() => import("./pages/inquiry"));
const ContactPage = React.lazy(() => import("./pages/contact"));
const BulkOrderPage = React.lazy(() => import("./pages/bulk-order"));
const BlogPage = React.lazy(() => import("./pages/blogs"));
const TestimonialsPage = React.lazy(() => import("./pages/testimonials"));
const ServiceTypesPage = React.lazy(() => import("./pages/service-types"));
const AmcPackagesPage = React.lazy(() => import("./pages/amc-packages"));
const SEOPage = React.lazy(() => import("./pages/seo"));
const SettingsPage = React.lazy(() => import("./pages/settings"));
const AmcOrdersPage = React.lazy(() => import("./pages/amc-orders"));
const LandingPagesPage = React.lazy(() => import("./pages/landing-pages"));

const LoginPage = React.lazy(() => import("./pages/login"));
const ForgotPassword = React.lazy(() => import("./pages/login/forgotpassword"));

const Page401 = React.lazy(() => import("./pages/401"));
const Page404 = React.lazy(() => import("./pages/404"));

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>;
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>;
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>;
  }
};

function App() {
  const aclAbilities = defaultACLObj;

  return (
    <Suspense fallback={<FallbackSpinner />}>
      <AclGuard aclAbilities={aclAbilities}>
        <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path="/401" element={<Page401 />} />
            <Route path="/404" element={<Page404 />} />

            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path="/login" element={<LoginPage />}></Route>
                <Route
                  path="/forgot-password"
                  element={<ForgotPassword />}
                ></Route>
              </Route>
            </Route>
          </Route>

          <Route
            element={
              <UserLayout>
                <Outlet />
              </UserLayout>
            }
          >
            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path="" element={<HomePage />} />
              <Route path="/dashboard" element={<DashBoardPage />} />
              <Route path="/home-details" element={<HomeDetailsPage />} />
              <Route path="/landing-pages" element={<LandingPagesPage />} />
              <Route path="/service-types" element={<ServiceTypesPage />} />
              <Route path="/amc-packages" element={<AmcPackagesPage />} />
              <Route path="/banner-images" element={<BannerImagesPage />} />
              <Route path="/performax" element={<PerformaxPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/workshop" element={<WorkshopPage />} />
              <Route path="/service" element={<ServicePage />} />
              <Route path="/inquiry" element={<InquiryPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/bulk-orders" element={<BulkOrderPage />} />
              <Route path="/blogs" element={<BlogPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/seo" element={<SEOPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/amc-orders" element={<AmcOrdersPage />} />
            </Route>
          </Route>

          {/* If no route found redirect it to --> /404 */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </AclGuard>
    </Suspense>
  );
}

export default App;
